// useBadgeRealtime — app-level subscriber realtime cho sidebar badge.
//
// Vì sao: badge (task/approval/ticket... assign cho mình bởi NGƯỜI KHÁC) trước đây chỉ poll
// 60s mới thấy. Server (realtimeNs `/realtime`) emit `badge:refresh` tới `user:<id>` khi có
// assign → FE refresh badge TỨC THÌ. Poll giữ làm lưới an toàn (time-based overdue + event lỡ).
//
// Mount 1 LẦN ở AppLayout (sidebar luôn hiện). Connect `/realtime` namespace — server tự join
// socket vào room `user:<id>` + `tenant:<id>` theo JWT.

import { useEffect } from 'react';
import { io } from 'socket.io-client';
import { triggerSidebarBadgeRefresh } from './useSidebarBadges';
import { refreshBadgeCounts } from './useBadgeCounts';

let _sock = null;

function resolveSocketUrl() {
  const env = (typeof import.meta !== 'undefined' && import.meta.env) ? import.meta.env : {};
  if (env.VITE_SOCKET_URL) return env.VITE_SOCKET_URL;
  if (env.VITE_API_BASE_URL) {
    try {
      const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      const u = new URL(env.VITE_API_BASE_URL, base);
      return `${u.protocol}//${u.host}`;
    } catch { /* fallback */ }
  }
  return typeof window !== 'undefined' ? window.location.origin : '';
}

function getToken() {
  try {
    const t = localStorage.getItem('access_token');
    if (t) return t;
    const raw = localStorage.getItem('cnvwork-saas-auth');
    if (raw) { const p = JSON.parse(raw); return p?.state?.accessToken || p?.accessToken || null; }
  } catch { /* ignore */ }
  return null;
}

/** Socket `/realtime` dùng CHUNG (một kết nối cho cả app) — bàn Xếp việc nghe chuông `work_schedule:changed` trên chính nó. */
export function getRealtimeSocket() {
  if (_sock) return _sock;
  const token = getToken();
  if (!token) return null;
  _sock = io(`${resolveSocketUrl()}/realtime`, {
    path: '/ws',
    // auth dạng FUNCTION → socket.io đọc lại token MỖI lần (re)connect. Quan trọng: sau khi
    // inboxClient refresh JWT (token cũ chết), reconnect dùng token mới — không kẹt invalid_token.
    auth: (cb) => cb({ token: getToken() }),
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 3000,
    reconnectionAttempts: Infinity,
  });
  _sock.on('connect_error', (err) => console.warn('[badge-realtime]', err.message));
  return _sock;
}

let _debounce = null;
export default function useBadgeRealtime() {
  useEffect(() => {
    const sock = getRealtimeSocket();
    if (!sock) return undefined;

    // Debounce 800ms — gộp nhiều event gần nhau (vd assign batch) thành 1 lần refresh.
    const onBadge = () => {
      if (_debounce) return;
      _debounce = setTimeout(() => {
        _debounce = null;
        triggerSidebarBadgeRefresh();   // sidebar (task/approval/ticket/chat/recruitment)
        try { refreshBadgeCounts(); } catch { /* best-effort */ } // CRM (deal/quotation/payment)
      }, 800);
    };
    sock.on('badge:refresh', onBadge);
    // Khi reconnect (mất rồi nối lại) → refresh 1 lần (có thể đã lỡ event lúc offline).
    sock.on('connect', onBadge);

    return () => {
      sock.off('badge:refresh', onBadge);
      sock.off('connect', onBadge);
    };
  }, []);
}
